import * as React from "react";
import {useHistory} from "react-router-dom";
import isNil from "lodash/isNil";
import {Box, Form, SubmitButton, Tiles, Text} from "@unibuddy/patron";
import {string} from "yup";
import {useMutation} from "react-apollo";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import {useTranslation} from "@unibuddy/intl";
import styled from "styled-components";

import {
    ProspectStage,
    ProspectStageLocation,
    prospectStageMutation,
    ProspectStagesForUi,
} from "../../Chat/components/PopupQuestions/prospectStages";
import {DecisionStageTile} from "./DecisionStageTile";
import {useUniversity} from "../../UniversityProvider/UniversityProvider";
import useUniversityColor from "../../UniversityProvider/hooks/useUniversityColor";

const DecisionStageFormField = {
    DECISION_STAGE: "decisionStage",
};

const validationSchema = {
    [DecisionStageFormField.DECISION_STAGE]: string().required(
        "Please provide where you are in your journey",
    ),
};

const StyledSubmitButton = styled(SubmitButton)`
    &:hover:not([disabled]) {
        cursor: pointer;
        opacity: 0.85;
    }
`;

export type DecisionStageFormProps = {
    handleSubmit(values: DecisionStageFormValues): void | Promise<void>;
};

export type DecisionStageFormContainerProps = {
    decisionStageLocation: ProspectStageLocation;
};

type DecisionStageFormValues = {
    decisionStage: ProspectStage | null;
};

type SetFieldValueCallBack = (field: string, newValue: ProspectStage) => void;

type DecisionStageFormInnerProps = {
    values: DecisionStageFormValues;
    setFieldValue: SetFieldValueCallBack;
};
/**
 * UI for decision stages
 */
export const DecisionStageForm: React.FC<DecisionStageFormProps> = (props) => {
    const universityColor = useUniversityColor();
    const {t} = useTranslation("decisionStage");

    return (
        <Form
            onSubmit={(values: DecisionStageFormValues, {setSubmitting}) => {
                setSubmitting(true);
                props.handleSubmit(values);
            }}
            initialValues={{[DecisionStageFormField.DECISION_STAGE]: null}}
            validationSchema={validationSchema}
        >
            {(formProps: DecisionStageFormInnerProps) => {
                const decisionStage = formProps.values[DecisionStageFormField.DECISION_STAGE];
                const isButtonDisabled = isNil(decisionStage);

                return (
                    <Box>
                        <Box
                            borderWidth={1}
                            borderRadius="xxsmall"
                            borderColor="#D0D9D9"
                            padding="medium"
                            marginBottom="medium"
                        >
                            <Tiles columns={[1, 2, 3]} space="medium">
                                {ProspectStagesForUi.map((stage) => (
                                    <Box
                                        key={stage.value}
                                        onClick={() => {
                                            formProps.setFieldValue(
                                                DecisionStageFormField.DECISION_STAGE,
                                                stage.value,
                                            );
                                        }}
                                    >
                                        <DecisionStageTile
                                            isActive={stage.value === decisionStage}
                                            decisionStageName={t(stage.translationKey, stage.name)}
                                        />
                                    </Box>
                                ))}
                            </Tiles>
                        </Box>
                        <StyledSubmitButton
                            block
                            disabled={isButtonDisabled}
                            style={{backgroundColor: universityColor}}
                        >
                            <Text color="white">
                                {t("viewRecommendations", "View recommendations")}
                            </Text>
                        </StyledSubmitButton>
                    </Box>
                );
            }}
        </Form>
    );
};

/*
 * Wraps the form with specific functionality
 */
export const DecisionStageFormContainer: React.FC<DecisionStageFormContainerProps> = (props) => {
    const university = useUniversity();
    const {trackEvent} = useAnalytics();
    const history = useHistory();
    const [saveUserDecisionStage] = useMutation(prospectStageMutation);

    const handleSubmit = React.useCallback(
        async (values: DecisionStageFormValues) => {
            const [universityId, universityName, decisionStage, prospectStageLocation] = [
                university.id,
                university.name,
                values.decisionStage,
                props.decisionStageLocation,
            ];

            try {
                await saveUserDecisionStage({
                    variables: {
                        universityId,
                        decisionStage,
                        prospectStageLocation,
                    },
                });
            } catch (e) {
                console.error("Error submitting decisionStage form", e);
            }

            trackEvent(sharedEvents.prospectDecisionStageSubmitted.name, {
                universityId,
                universityName,
                prospectStage: decisionStage,
                where: prospectStageLocation,
            });

            history.push(`/pwa/${university.slug}/recommendations`);
        },
        [
            props.decisionStageLocation,
            trackEvent,
            university,
            props,
            history,
            saveUserDecisionStage,
        ],
    );

    return <DecisionStageForm handleSubmit={handleSubmit} />;
};
